<template>
	<div
		class="inline-flex items-stretch"
		:class="{
			'is-active': isActive,
			'dropdown-button': !disabled,
			'dropdown-button-disabled': disabled,
			'dropdown-button-row': direction === 'row',
			'dropdown-button-column': direction === 'column',
			'dropdown-button-has-options': hasOptions,
			'dropdown-button-no-options': !hasOptions,
			'dropdown-button-icon-only': iconOnly,
		}"
	>
		<n-popover trigger="hover" placement="bottom" :disabled="!hint" style="max-width: 260px">
			<template #trigger>
				<div class="dropdown-button-main" @click="handleClick">
					<div class="dropdown-button-label" :class="{ 'is-row': direction === 'row', 'is-column': direction !== 'row' }">
						<SvgIcon :content="icon" :alt="iconAltText" :title="iconOnly ? label : undefined" :size="wh" />
						<span v-if="!iconOnly && displayLabel">{{ displayLabel }}</span>
					</div>
				</div>
			</template>

			<div v-if="hint">
				<b v-if="hint.label || label" class="text-14px">{{ hint.label || label }}</b>

				<n-image width="220" v-if="hint.image" :src="hint.image" preview-disabled class="block my-2" />

				<n-p v-if="hint.desc" class="text-12px">{{ hint.desc }}</n-p>
			</div>
		</n-popover>

		<n-dropdown v-if="hasOptions" trigger="click" :disabled="disabled" :options="options" :z-index="floatingPanelDropdownZIndex" @select="handleSelect">
			<div class="dropdown-button-down" :class="{ 'is-active': isActive }">
				<span class="dropdown-button-down-icon">▾</span>
			</div>
		</n-dropdown>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import SvgIcon from "./SvgIcon.vue";
	import { useFloatingPanelOverlayZIndex } from "@/composables/dialog/useFloatingPanelOverlayContext";

	const props = withDefaults(
		defineProps<{
			/** 是否处于激活态 */
			isActive?: boolean;
			/** 是否禁用 */
			disabled?: boolean;
			/** 下拉选项列表 */
			options?: any;
			/** 按钮图标 SVG 原始标记（由 `@/assets/icon/xx.svg?raw` 导入） */
			icon: string;
			/** 按钮文字标签；iconOnly 时仍用于无障碍与悬停提示 */
			label?: string;
			/** 提示信息 */
			hint?: {
				/** 提示标题 */
				label?: string;
				/** 提示文本对应的图片，一般是gif动态图 */
				image?: string;
				/* 提示文本详细说明 */
				desc?: string;
			};
			/** 提示文本对应的图片，一般是gif动态图 */
			hintImage?: string;
			/** 按钮尺寸 */
			size?: "small" | "medium" | "large";
			/** 布局方向 */
			direction?: "row" | "column";
			/** 是否仅显示图标，不显示文字标签 */
			iconOnly?: boolean;
		}>(),
		{
			isActive: false,
			disabled: false,
			options: () => [],
			icon: "",
			label: "",
			size: "medium",
			direction: "column",
			iconOnly: false,
		}
	);

	const emit = defineEmits<{
		(e: "select", key: string): void;
		(e: "click"): void;
	}>();

	const hasOptions = computed(() => Array.isArray(props.options) && props.options.length > 0);

	/**
	 * 浮动面板内下拉菜单 z-index。
	 *
	 * 设计说明：FloatingPanel 基底 z-index 为 4000，Naive Dropdown 默认层级更低，
	 * 在浮动面板子树内使用时自动抬升，避免菜单被面板遮挡。
	 */
	const floatingPanelDropdownZIndex = useFloatingPanelOverlayZIndex();

	/** 图标 alt 文案：优先使用 label，缺省时回退为通用占位 */
	const iconAltText = computed(() => props.label || "toolbar-button");

	const displayLabel = computed(() => {
		const label = props.label ?? "";
		if (!label) {
			return "";
		}
		const shouldFormatThreeChars = props.size === "large" && props.direction === "column" && !hasOptions.value;
		if (!shouldFormatThreeChars) {
			return label;
		}
		const chineseCharPattern = /[\u4e00-\u9fff]/;
		if (!chineseCharPattern.test(label)) {
			return label;
		}

		let chineseCount = 0;
		let formatted = "";
		for (const char of label) {
			if (chineseCharPattern.test(char) && chineseCount > 0 && chineseCount % 3 === 0) {
				formatted += "\n";
			}
			formatted += char;
			if (chineseCharPattern.test(char)) {
				chineseCount += 1;
			}
		}
		return formatted;
	});

	const wh = computed(() => {
		switch (props.size) {
			case "small":
				return "22px";
			case "large":
				return "40px";
			case "medium":
			default:
				return "30px";
		}
	});

	function handleSelect(key: string) {
		if (props.disabled) return;

		emit("select", key);
	}

	function handleClick() {
		if (props.disabled) return;

		emit("click");
	}
</script>

<style scoped lang="less">
	.dropdown-button {
		border: 1px solid transparent;
		overflow: hidden;
		transition: all 0.2s;

		.dropdown-button-main {
			cursor: pointer;
		}

		&-label {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 2px;
			padding: 6px 8px;
		}

		&-down {
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: 14px;
			// height: 100%;
			padding: 8px 3px;
			cursor: pointer;

			&-icon {
				font-size: 12px;
				opacity: 0.7;
				line-height: 0.8;
				transform: scale(1.5);
			}
		}

		&:hover,
		&.is-active {
			background: var(--n-tab-color);
			box-shadow: inset 0 0 0 1px var(--n-tab-border-color);
			border-color: var(--n-tab-border-color); // --n-tab-text-color-disabled
			cursor: pointer;

			.dropdown-button-down {
				background: var(--n-tab-color-segment);
			}
		}
	}

	.dropdown-button-row {
		.dropdown-button-label {
			padding: 3px 6px;
		}
		.dropdown-button-down {
			min-width: 13px;
			padding: 0 5px;
		}
	}

	.dropdown-button-column {
		flex-direction: column;
	}

	.dropdown-button-label.is-column {
		flex-direction: column;

		span {
			white-space: pre-line;
			word-break: keep-all;
			text-align: center;
		}
	}

	.dropdown-button-label.is-row {
		flex-direction: row;
		justify-content: flex-start;
	}

	.dropdown-button-icon-only {
		.dropdown-button-label {
			padding: 3px 4px;
			justify-content: center;
		}
	}

	.dropdown-button-disabled {
		border: 1px solid transparent;
		overflow: hidden;

		.dropdown-button-main {
			cursor: not-allowed;
		}

		:deep(.puppy-svg-icon) {
			filter: grayscale(100%) brightness(1.2);
		}

		span {
			color: var(--n-tab-text-color-disabled);
		}

		.dropdown-button-down {
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: 14px;
			border-left: 1px solid transparent;
			cursor: not-allowed;

			&-icon {
				font-size: 12px;
				opacity: 0.7;
			}
		}
	}
</style>
